import { type RefObject, useEffect, useState } from 'react'

/** The stage is laid out at one fixed width and scaled to whatever it's dropped into. */
export const STAGE_WIDTH = 1200

/** Reserved before anything is measured, so the first play doesn't grow the block much. */
const SEED_HEIGHT = 840

interface StageFit {
  /** Uniform scale applied to the stage, never above 1 — it shrinks, it doesn't blow up. */
  scale: number
  /** Unscaled stage height — the block's aspect ratio, and the stage's own floor between scenes. */
  stageHeight: number
}

/**
 * Fits the fixed-width stage to its container. The measured height only ever grows:
 * the focused card animates its own height open, and a block that tracked that exactly
 * would shove the rest of the page up and down on every loop.
 */
export function useStageScale(
  containerRef: RefObject<HTMLElement | null>,
  stageRef: RefObject<HTMLElement | null>
): StageFit {
  const [scale, setScale] = useState(1)
  const [stageHeight, setStageHeight] = useState(SEED_HEIGHT)

  useEffect(() => {
    const container = containerRef.current
    if (!container || typeof ResizeObserver === 'undefined') return

    const observer = new ResizeObserver(([entry]) => {
      setScale(Math.min(1, entry.contentRect.width / STAGE_WIDTH))
    })
    observer.observe(container)
    return () => observer.disconnect()
  }, [containerRef])

  useEffect(() => {
    const stage = stageRef.current
    if (!stage || typeof ResizeObserver === 'undefined') return

    const observer = new ResizeObserver(([entry]) => {
      setStageHeight((current) => Math.max(current, Math.ceil(entry.contentRect.height)))
    })
    observer.observe(stage)
    return () => observer.disconnect()
  }, [stageRef])

  return { scale, stageHeight }
}
